import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { PlusIcon, MagnifyingGlassIcon, FunnelIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ArticleSelector from '../components/ArticleSelector';
import promotionsService, { Promotion as ApiPromotion } from '../services/promotionsService';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import ArticlePromotionView from '../components/ArticlePromotionView';

// Dynamic article type for promotion-specific data
type DynamicArticle = Record<string, any>;

// Use the promotion type from the service, but extend it with computed status
interface Promotion extends Omit<ApiPromotion, 'is_active'> {
  status: 'en_cours' | 'termine' | 'en_attente' | 'annule';
}

interface CreatePromotionForm {
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  filters: Record<string, any>;
}

type TabType = 'promotions' | 'article-promotion';

const Promotions: React.FC = () => {
  const { activeDatabase } = useDatabaseContext();
  const [activeTab, setActiveTab] = useState<TabType>('promotions');
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'en_cours' | 'termine' | 'en_attente' | 'annule'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showAffectedArticlesModal, setShowAffectedArticlesModal] = useState(false);
  const [previewArticles, setPreviewArticles] = useState<DynamicArticle[]>([]);
  const [affectedArticles, setAffectedArticles] = useState<DynamicArticle[]>([]);
  const [viewingPromotion, setViewingPromotion] = useState<Promotion | null>(null);
  const [computedArticleCounts, setComputedArticleCounts] = useState<Record<number, number>>({});
  const [computingArticles, setComputingArticles] = useState<Record<number, boolean>>({});
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [formData, setFormData] = useState<CreatePromotionForm>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    filters: {}
  });
  const [editFormData, setEditFormData] = useState<{
    name: string;
    description: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
    filters: Record<string, any>;
  }>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    is_active: true,
    filters: {}
  });
  const [filteredArticlesForSelection, setFilteredArticlesForSelection] = useState<DynamicArticle[]>([]);
  const [selectedArticleIds, setSelectedArticleIds] = useState<string[]>([]);
  const [selectedArticlesData, setSelectedArticlesData] = useState<DynamicArticle[]>([]);
  // State for create modal article selection
  const [createFilteredArticlesForSelection, setCreateFilteredArticlesForSelection] = useState<DynamicArticle[]>([]);
  const [createSelectedArticleIds, setCreateSelectedArticleIds] = useState<string[]>([]);
  const [createSelectedArticlesData, setCreateSelectedArticlesData] = useState<DynamicArticle[]>([]);

  // Mock data for development
  const mockPromotions: Promotion[] = useMemo(() => [
    {
      id: 1,
      name: "Promotion d'été 2024",
      description: "Réduction sur tous les articles de la catégorie été",
      start_date: '2024-06-01',
      end_date: '2024-08-31',
      status: 'en_cours',
      database_name: activeDatabase?.name || 'default',
      filters: { category: 'été', price_min: 50 },
      created_at: '2024-05-15T10:00:00Z',
      updated_at: '2024-05-15T10:00:00Z'
    },
    {
      id: 2,
      name: "Liquidation stock ancien",
      description: "Réduction fixe sur les articles en fin de vie",
      start_date: '2024-01-01',
      end_date: '2024-12-31',
      status: 'en_cours',
      database_name: activeDatabase?.name || 'default',
      filters: { 'Cycle de vie': ['decline'] },
      created_at: '2024-01-01T10:00:00Z',
      updated_at: '2024-03-15T10:00:00Z'
    },
    {
      id: 3,
      name: "Promotion Black Friday",
      description: "Promotion programmée pour le Black Friday",
      start_date: '2024-11-24',
      end_date: '2024-11-30',
      status: 'en_attente',
      database_name: activeDatabase?.name || 'default',
      filters: { price_min: 100 },
      created_at: '2024-10-01T10:00:00Z',
      updated_at: '2024-10-01T10:00:00Z'
    }
  ], [activeDatabase?.name]);

  // Load promotions from API
  const loadPromotions = useCallback(async () => {
    if (!activeDatabase?.id) return;
    
    try {
      setLoading(true);
      const data = await promotionsService.getPromotions(activeDatabase.id);
      // Convert API promotions to UI promotions with computed status
      const uiPromotions: Promotion[] = data.map(apiPromotion => ({
        ...apiPromotion,
        status: computePromotionStatus(apiPromotion),
        article_ids: apiPromotion.article_ids || [] // Ensure article_ids is always an array
      }));
      setPromotions(uiPromotions);
      
      // Pre-populate article counts from article_ids
      uiPromotions.forEach(promotion => {
        if (promotion.article_ids && promotion.article_ids.length > 0) {
          setComputedArticleCounts(prev => ({ ...prev, [promotion.id]: promotion.article_ids!.length }));
        }
      });
    } catch (error) {
      console.error('Error loading promotions:', error);
      // Fallback to mock data for development
      setPromotions(mockPromotions);
    } finally {
      setLoading(false);
    }
  }, [activeDatabase?.id, mockPromotions]);

  useEffect(() => {
    if (activeDatabase?.id) {
      loadPromotions();
    }
  }, [activeDatabase?.id, loadPromotions]);

  const computePromotionStatus = (promotion: ApiPromotion): 'en_cours' | 'termine' | 'en_attente' | 'annule' => {
    if (!promotion.is_active) return 'annule';
    
    const now = new Date();
    const startDate = new Date(promotion.start_date);
    const endDate = new Date(promotion.end_date);
    
    if (now < startDate) return 'en_attente';
    if (now > endDate) return 'termine';
    return 'en_cours';
  };

  const getPromotionStatus = (promotion: Promotion) => {
    if (promotion.status === 'annule') return 'annule';
    
    const now = new Date();
    const startDate = new Date(promotion.start_date);
    const endDate = new Date(promotion.end_date);
    
    if (now < startDate) return 'en_attente';
    if (now > endDate) return 'termine';
    return 'en_cours';
  };

  const getStatusBadge = (promotion: Promotion) => {
    const status = getPromotionStatus(promotion);
    const badges = {
      en_cours: 'bg-green-100 text-green-800',
      en_attente: 'bg-blue-100 text-blue-800',
      termine: 'bg-gray-100 text-gray-800',
      annule: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      en_cours: 'En cours',
      en_attente: 'En attente',
      termine: 'Terminé',
      annule: 'Annulé'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const handleDeletePromotion = async (promotion: Promotion) => {
    if (!activeDatabase) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer la promotion "${promotion.name}" ?`)) {
      try {
        setLoading(true);
        await promotionsService.deletePromotion(activeDatabase.id, promotion.id);
        setPromotions(prev => prev.filter(p => p.id !== promotion.id));
      } catch (error) {
        console.error('Error deleting promotion:', error);
        alert('Erreur lors de la suppression de la promotion');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditPromotion = async (promotion: Promotion) => {
    setEditingPromotion(promotion);
    const initialFilters = promotion.filters || {};
    const initialArticleIds = promotion.article_ids || [];
    setEditFormData({
      name: promotion.name,
      description: promotion.description,
      start_date: promotion.start_date,
      end_date: promotion.end_date,
      is_active: promotion.status !== 'annule',
      filters: initialFilters
    });
    setFilteredArticlesForSelection([]);
    setSelectedArticleIds(initialArticleIds);
    
    // Load selected articles data if we have article_ids
    if (initialArticleIds.length > 0 && activeDatabase?.id) {
      try {
        const response = await api.post(
          API_ROUTES.DB_MANAGER.BASE + API_ROUTES.DB_MANAGER.ARTICLES_BY_IDS(activeDatabase.id),
          { article_ids: initialArticleIds }
        );
        const articles = Array.isArray(response.data) ? response.data : [];
        // Convert to DynamicArticle format
        const dynamicArticles: DynamicArticle[] = articles.map((article: any) => ({
          id: article.ref || article.id,
          ref: article.ref || article.id,
          nom: article.nom || article.name,
          ...article
        }));
        setSelectedArticlesData(dynamicArticles);
      } catch (error) {
        console.error('Error loading selected articles:', error);
        setSelectedArticlesData([]);
      }
    } else {
      setSelectedArticlesData([]);
    }
    
    setShowEditModal(true);
  };

  const handleEditFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingPromotion || !activeDatabase) return;
    
    // Validate form
    if (!editFormData.name || !editFormData.description || !editFormData.start_date || !editFormData.end_date) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (new Date(editFormData.start_date) >= new Date(editFormData.end_date)) {
      alert('La date de fin doit être postérieure à la date de début');
      return;
    }

    try {
      setLoading(true);
      
      // Update promotion via API
      const updatedApiPromotion = await promotionsService.updatePromotion(activeDatabase.id, editingPromotion.id, {
        name: editFormData.name,
        description: editFormData.description,
        start_date: editFormData.start_date,
        end_date: editFormData.end_date,
        is_active: editFormData.is_active,
        filters: editFormData.filters,
        article_ids: selectedArticleIds
      });

      // Convert to UI promotion with recalculated status
      const updatedPromotion: Promotion = {
        ...updatedApiPromotion,
        status: computePromotionStatus(updatedApiPromotion)
      };

      // Update computed article count based on selected article IDs
      setComputedArticleCounts(prev => ({ ...prev, [editingPromotion.id]: selectedArticleIds.length }));

      // Update the promotions list
      setPromotions(prev => prev.map(p => 
        p.id === editingPromotion.id ? updatedPromotion : p
      ));
      
      // Reset form and close modal
      setEditFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        is_active: true,
        filters: {}
      });
      setFilteredArticlesForSelection([]);
      setSelectedArticleIds([]);
      setSelectedArticlesData([]);
      setEditingPromotion(null);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating promotion:', error);
      alert('Erreur lors de la mise à jour de la promotion');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      is_active: true,
      filters: {}
    });
    setFilteredArticlesForSelection([]);
    setSelectedArticleIds([]);
    setSelectedArticlesData([]);
    setEditingPromotion(null);
    setShowEditModal(false);
  };

  // Article management functions
  const handleAddAllFilteredArticles = () => {
    const newArticleIds = filteredArticlesForSelection
      .map(article => String(article.id || article.ref))
      .filter(id => !selectedArticleIds.includes(id));
    
    setSelectedArticleIds(prev => [...prev, ...newArticleIds]);
    setSelectedArticlesData(prev => [...prev, ...filteredArticlesForSelection.filter(article => {
      const id = String(article.id || article.ref);
      return !prev.some(existing => String(existing.id || existing.ref) === id);
    })]);
  };

  const handleAddArticle = (article: DynamicArticle) => {
    const articleId = String(article.id || article.ref);
    if (!selectedArticleIds.includes(articleId)) {
      setSelectedArticleIds(prev => [...prev, articleId]);
      setSelectedArticlesData(prev => [...prev, article]);
    }
  };

  const handleRemoveArticle = (articleId: string) => {
    setSelectedArticleIds(prev => prev.filter(id => id !== articleId));
    setSelectedArticlesData(prev => prev.filter(article => String(article.id || article.ref) !== articleId));
  };

  const handleRemoveAllArticles = () => {
    setSelectedArticleIds([]);
    setSelectedArticlesData([]);
  };

  // Article management functions for create modal
  const handleCreateAddAllFilteredArticles = () => {
    const newArticleIds = createFilteredArticlesForSelection
      .map(article => String(article.id || article.ref))
      .filter(id => !createSelectedArticleIds.includes(id));
    
    setCreateSelectedArticleIds(prev => [...prev, ...newArticleIds]);
    setCreateSelectedArticlesData(prev => [...prev, ...createFilteredArticlesForSelection.filter(article => {
      const id = String(article.id || article.ref);
      return !prev.some(existing => String(existing.id || existing.ref) === id);
    })]);
  };

  const handleCreateAddArticle = (article: DynamicArticle) => {
    const articleId = String(article.id || article.ref);
    if (!createSelectedArticleIds.includes(articleId)) {
      setCreateSelectedArticleIds(prev => [...prev, articleId]);
      setCreateSelectedArticlesData(prev => [...prev, article]);
    }
  };

  const handleCreateRemoveArticle = (articleId: string) => {
    setCreateSelectedArticleIds(prev => prev.filter(id => id !== articleId));
    setCreateSelectedArticlesData(prev => prev.filter(article => String(article.id || article.ref) !== articleId));
  };

  const handleCreateRemoveAllArticles = () => {
    setCreateSelectedArticleIds([]);
    setCreateSelectedArticlesData([]);
  };


  const fetchAffectedArticles = async (promotion: Promotion) => {
    if (!activeDatabase) return;

    try {
      setComputingArticles(prev => ({ ...prev, [promotion.id]: true }));
      
      // Use article_ids if available, otherwise fall back to filters for backward compatibility
      const articleIds = promotion.article_ids || [];
      let articles: DynamicArticle[] = [];
      
      if (articleIds.length > 0) {
        // Use article_ids to fetch articles
        const response = await api.post(
          API_ROUTES.DB_MANAGER.BASE + API_ROUTES.DB_MANAGER.ARTICLES_BY_IDS(activeDatabase.id),
          { article_ids: articleIds }
        );
        
        if (response.status !== 200) {
          throw new Error('Failed to fetch articles by IDs');
        }
        
        const data = Array.isArray(response.data) ? response.data : [];
        articles = data.map((article: any) => ({
          id: article.ref || article.id,
          ref: article.ref || article.id,
          nom: article.nom || article.name,
          ...article
        }));
      } else if (promotion.filters && Object.keys(promotion.filters).length > 0) {
        // Fallback to filters for backward compatibility
        const response = await api.post(
          API_ROUTES.DB_MANAGER.BASE + API_ROUTES.DB_MANAGER.FILTERED_ARTICLES(activeDatabase.id),
          { filters: promotion.filters }
        );

        if (response.status !== 200) {
          throw new Error('Failed to fetch filtered articles');
        }

        const data = response.data;
        articles = data.articles || [];
      }
      
      // Store the computed count
      setComputedArticleCounts(prev => ({ ...prev, [promotion.id]: articles.length }));
      
      // Set articles for modal display
      setAffectedArticles(articles);
      setViewingPromotion(promotion);
      setShowAffectedArticlesModal(true);
      
    } catch (error) {
      console.error('Error fetching affected articles:', error);
      alert('Erreur lors du chargement des articles affectés');
    } finally {
      setComputingArticles(prev => ({ ...prev, [promotion.id]: false }));
    }
  };

  const getAffectedArticlesDisplay = (promotion: Promotion) => {
    const isComputing = computingArticles[promotion.id];
    const computedCount = computedArticleCounts[promotion.id];
    
    // Use article_ids count if available, otherwise use computed count
    const articleCount = promotion.article_ids?.length || computedCount;
    
    if (isComputing) {
      return <span className="text-gray-500">Chargement...</span>;
    }
    
    if (articleCount !== undefined && articleCount > 0) {
      return (
        <button
          onClick={() => fetchAffectedArticles(promotion)}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {articleCount} articles
        </button>
      );
    }
    
    return (
      <button
        onClick={() => fetchAffectedArticles(promotion)}
        className="text-blue-600 hover:text-blue-800 underline"
      >
        Voir les articles affectés
      </button>
    );
  };

  // Utility function to get dynamic columns from articles data
  const getDynamicColumns = (articles: DynamicArticle[]) => {
    if (!articles || articles.length === 0) return [];
    
    // Get all unique field names from the articles
    const allFields = new Set<string>();
    articles.forEach(article => {
      Object.keys(article).forEach(key => allFields.add(key));
    });
    
    // Convert to array and prioritize ref and nom as first columns
    const fieldArray = Array.from(allFields);
    const prioritizedColumns = [];
    
    // Always put ref and nom first if they exist
    if (fieldArray.includes('ref')) {
      prioritizedColumns.push('ref');
    }
    if (fieldArray.includes('nom')) {
      prioritizedColumns.push('nom');
    }
    
    // Add remaining fields (excluding ref and nom since they're already added)
    const remainingFields = fieldArray.filter(field => field !== 'ref' && field !== 'nom');
    prioritizedColumns.push(...remainingFields);
    
    return prioritizedColumns;
  };

  // Utility function to get display name for a column
  const getColumnDisplayName = (fieldName: string) => {
    const displayNames: Record<string, string> = {
      ref: 'Référence',
      nom: 'Nom',
      unite: 'Unité',
      contenance: 'Contenance',
      code_espece: 'Espèce',
      code_sous_famille: 'Sous-famille',
      cout_unitaire: 'Coût unitaire',
      prix_vente: 'Prix de vente',
      categorie: 'Catégorie',
      couleur: 'Couleur',
      matiere: 'Matière',
      // Add more mappings as needed
    };
    
    return displayNames[fieldName] || fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  };

  // Utility function to format cell value
  const formatCellValue = (value: any, fieldName: string) => {
    if (value === null || value === undefined) return '-';
    
    // Format specific field types
    if (fieldName === 'cout_unitaire' || fieldName === 'prix_vente') {
      return typeof value === 'number' ? `${value.toFixed(2)}€` : value;
    }
    
    if (fieldName === 'contenance') {
      return typeof value === 'number' ? value.toFixed(1) : value;
    }
    
    return String(value);
  };

  const filteredPromotions = promotions.filter(promotion => {
    const matchesSearch = promotion.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         promotion.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === 'all') return matchesSearch;
    
    const status = getPromotionStatus(promotion);
    return matchesSearch && status === statusFilter;
  });

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.description || !formData.start_date || !formData.end_date) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      alert('La date de fin doit être postérieure à la date de début');
      return;
    }

    if (!activeDatabase) {
      alert('Aucune base de données active sélectionnée');
      return;
    }

    try {
      setLoading(true);
      
      // Create new promotion via API
      const newApiPromotion = await promotionsService.createPromotion(activeDatabase.id, {
        name: formData.name,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date,
        filters: formData.filters,
        article_ids: createSelectedArticleIds
      });

      // Convert to UI promotion
      const newPromotion: Promotion = {
        ...newApiPromotion,
        status: computePromotionStatus(newApiPromotion)
      };

      // Store the computed article count (will be 0 for new promotions as article_ids starts empty)
      setComputedArticleCounts(prev => ({ ...prev, [newApiPromotion.id]: newApiPromotion.article_ids?.length || 0 }));

      setPromotions(prev => [newPromotion, ...prev]);
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        filters: {}
      });
      setPreviewArticles([]);
      setCreateFilteredArticlesForSelection([]);
      setCreateSelectedArticleIds([]);
      setCreateSelectedArticlesData([]);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating promotion:', error);
      alert('Erreur lors de la création de la promotion');
    } finally {
      setLoading(false);
    }
  };


  const handleCloseCreateModal = () => {
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      filters: {}
    });
    setPreviewArticles([]);
    setCreateFilteredArticlesForSelection([]);
    setCreateSelectedArticleIds([]);
    setCreateSelectedArticlesData([]);
    setShowCreateModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Promotions</h1>
          <p className="text-gray-600 mt-1">
            Gérez vos promotions et suivez leur impact sur les ventes
          </p>
        </div>
        {activeTab === 'promotions' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Nouvelle promotion
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('promotions')}
            className={`${
              activeTab === 'promotions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Promotions
          </button>
          <button
            onClick={() => setActiveTab('article-promotion')}
            className={`${
              activeTab === 'article-promotion'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Article-Promotion
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'article-promotion' ? (
        <ArticlePromotionView />
      ) : (
        <>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une promotion..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="en_cours">En cours</option>
              <option value="en_attente">En attente</option>
              <option value="termine">Terminé</option>
              <option value="annule">Annulé</option>
            </select>
          </div>
        </div>
      </div>

      {/* Promotions List */}
      <div className="bg-white rounded-xl shadow-sm">
        {filteredPromotions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-4">
              <FunnelIcon className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune promotion trouvée</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter !== 'all' 
                ? 'Aucune promotion ne correspond à vos critères de recherche.'
                : 'Commencez par créer votre première promotion.'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Créer une promotion
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Promotion
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Articles affectés
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPromotions.map((promotion) => {
                  return (
                    <tr key={promotion.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {promotion.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {promotion.description}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{new Date(promotion.start_date).toLocaleDateString('fr-FR')}</div>
                        <div className="text-gray-500">
                          au {new Date(promotion.end_date).toLocaleDateString('fr-FR')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getAffectedArticlesDisplay(promotion)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(promotion)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={() => handleEditPromotion(promotion)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Modifier la promotion"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeletePromotion(promotion)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer la promotion"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Promotion Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Créer une nouvelle promotion
              </h3>
              <button
                onClick={handleCloseCreateModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleFormSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la promotion *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    required
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Article Filters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filtres des articles
                </label>
                <ArticleSelector
                  onSelectionChange={() => {}} // Not needed here
                  onApplyFilters={(filters) => setFormData(prev => ({ ...prev, filters }))}
                  onArticlesChange={(articles) => {
                    // Convert Article[] to DynamicArticle[] for filtered articles selection
                    const dynamicArticles: DynamicArticle[] = articles.map(article => ({
                      id: article.id,
                      ref: article.id,
                      nom: article.name,
                      ...article.features?.reduce((acc, feature) => ({
                        ...acc,
                        [feature.name]: feature.value
                      }), {})
                    }));
                    setCreateFilteredArticlesForSelection(dynamicArticles);
                    setPreviewArticles(dynamicArticles); // Keep for backward compatibility
                  }}
                />
                
              </div>

              {/* Filtered Articles Section */}
              {createFilteredArticlesForSelection.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Articles filtrés ({createFilteredArticlesForSelection.length})
                    </label>
                    <button
                      type="button"
                      onClick={handleCreateAddAllFilteredArticles}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      Ajouter tous
                    </button>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {getDynamicColumns(createFilteredArticlesForSelection).slice(0, 5).map((columnName) => (
                            <th key={columnName} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              {getColumnDisplayName(columnName)}
                            </th>
                          ))}
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {createFilteredArticlesForSelection.map((article, index) => {
                          const articleId = String(article.id || article.ref);
                          const isSelected = createSelectedArticleIds.includes(articleId);
                          return (
                            <tr key={articleId || index} className={isSelected ? 'bg-gray-100' : ''}>
                              {getDynamicColumns(createFilteredArticlesForSelection).slice(0, 5).map((columnName) => (
                                <td key={columnName} className="px-3 py-2 text-sm text-gray-900">
                                  {formatCellValue(article[columnName], columnName)}
                                </td>
                              ))}
                              <td className="px-3 py-2 text-right text-sm">
                                <button
                                  type="button"
                                  onClick={() => handleCreateAddArticle(article)}
                                  disabled={isSelected}
                                  className={`text-blue-600 hover:text-blue-800 ${isSelected ? 'opacity-50 cursor-not-allowed' : 'underline'}`}
                                >
                                  {isSelected ? 'Ajouté' : 'Ajouter'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Selected Articles Section */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Articles sélectionnés pour la promotion ({createSelectedArticleIds.length})
                  </label>
                  {createSelectedArticleIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleCreateRemoveAllArticles}
                      className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                    >
                      Retirer tous
                    </button>
                  )}
                </div>
                {createSelectedArticlesData.length > 0 ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {getDynamicColumns(createSelectedArticlesData).slice(0, 5).map((columnName) => (
                            <th key={columnName} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              {getColumnDisplayName(columnName)}
                            </th>
                          ))}
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {createSelectedArticlesData.map((article, index) => {
                          const articleId = String(article.id || article.ref);
                          return (
                            <tr key={articleId || index}>
                              {getDynamicColumns(createSelectedArticlesData).slice(0, 5).map((columnName) => (
                                <td key={columnName} className="px-3 py-2 text-sm text-gray-900">
                                  {formatCellValue(article[columnName], columnName)}
                                </td>
                              ))}
                              <td className="px-3 py-2 text-right text-sm">
                                <button
                                  type="button"
                                  onClick={() => handleCreateRemoveArticle(articleId)}
                                  className="text-red-600 hover:text-red-800 underline"
                                >
                                  Retirer
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-4 text-center text-gray-500 text-sm">
                    Aucun article sélectionné. Utilisez les filtres ci-dessus pour ajouter des articles.
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseCreateModal}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Créer la promotion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Articles Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Articles affectés par la promotion ({previewArticles.length})
              </h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {previewArticles.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {getDynamicColumns(previewArticles).map((columnName) => (
                        <th key={columnName} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {getColumnDisplayName(columnName)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {previewArticles.map((article, index) => (
                      <tr key={article.id || article.ref || index}>
                        {getDynamicColumns(previewArticles).map((columnName) => (
                          <td key={columnName} className="px-4 py-2 text-sm text-gray-900">
                            {formatCellValue(article[columnName], columnName)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Aucun article à afficher
                </div>
              )}
            </div>
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

            {/* Edit Promotion Modal */}
      {showEditModal && editingPromotion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Modifier la promotion
              </h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleEditFormSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la promotion *
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    required
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    value={editFormData.start_date}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    value={editFormData.end_date}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={editFormData.is_active}
                      onChange={() => setEditFormData(prev => ({ ...prev, is_active: true }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!editFormData.is_active}
                      onChange={() => setEditFormData(prev => ({ ...prev, is_active: false }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Annulée</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Le statut final sera recalculé automatiquement en fonction des dates
                </p>
              </div>

               {/* Article Filters */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Filtres des articles
                 </label>
                 <ArticleSelector
                   onSelectionChange={() => {}} // Not needed here
                   onApplyFilters={(filters) => setEditFormData(prev => ({ ...prev, filters }))}
                   onArticlesChange={(articles) => {
                     // Convert Article[] to DynamicArticle[] for filtered articles
                     const dynamicArticles: DynamicArticle[] = articles.map(article => ({
                       id: article.id,
                       nom: article.name,
                       ref: article.id,
                       ...article.features?.reduce((acc, feature) => ({
                         ...acc,
                         [feature.name]: feature.value
                       }), {})
                     }));
                     setFilteredArticlesForSelection(dynamicArticles);
                   }}
                   selectedFilters={editFormData.filters}
                 />
                 
               </div>

               {/* Filtered Articles Section */}
               {filteredArticlesForSelection.length > 0 && (
                 <div className="mt-4">
                   <div className="flex items-center justify-between mb-2">
                     <label className="block text-sm font-medium text-gray-700">
                       Articles filtrés ({filteredArticlesForSelection.length})
                     </label>
                     <button
                       type="button"
                       onClick={handleAddAllFilteredArticles}
                       className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                     >
                       Ajouter tous
                     </button>
                   </div>
                   <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                     <table className="min-w-full divide-y divide-gray-200">
                       <thead className="bg-gray-50">
                         <tr>
                           {getDynamicColumns(filteredArticlesForSelection).slice(0, 5).map((columnName) => (
                             <th key={columnName} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                               {getColumnDisplayName(columnName)}
                             </th>
                           ))}
                           <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                         </tr>
                       </thead>
                       <tbody className="bg-white divide-y divide-gray-200">
                         {filteredArticlesForSelection.map((article, index) => {
                           const articleId = String(article.id || article.ref);
                           const isSelected = selectedArticleIds.includes(articleId);
                           return (
                             <tr key={articleId || index} className={isSelected ? 'bg-gray-100' : ''}>
                               {getDynamicColumns(filteredArticlesForSelection).slice(0, 5).map((columnName) => (
                                 <td key={columnName} className="px-3 py-2 text-sm text-gray-900">
                                   {formatCellValue(article[columnName], columnName)}
                                 </td>
                               ))}
                               <td className="px-3 py-2 text-right text-sm">
                                 <button
                                   type="button"
                                   onClick={() => handleAddArticle(article)}
                                   disabled={isSelected}
                                   className={`text-blue-600 hover:text-blue-800 ${isSelected ? 'opacity-50 cursor-not-allowed' : 'underline'}`}
                                 >
                                   {isSelected ? 'Ajouté' : 'Ajouter'}
                                 </button>
                               </td>
                             </tr>
                           );
                         })}
                       </tbody>
                     </table>
                   </div>
                 </div>
               )}

               {/* Selected Articles Section */}
               <div className="mt-4">
                 <div className="flex items-center justify-between mb-2">
                   <label className="block text-sm font-medium text-gray-700">
                     Articles sélectionnés pour la promotion ({selectedArticleIds.length})
                   </label>
                   {selectedArticleIds.length > 0 && (
                     <button
                       type="button"
                       onClick={handleRemoveAllArticles}
                       className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                     >
                       Retirer tous
                     </button>
                   )}
                 </div>
                 {selectedArticlesData.length > 0 ? (
                   <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                     <table className="min-w-full divide-y divide-gray-200">
                       <thead className="bg-gray-50">
                         <tr>
                           {getDynamicColumns(selectedArticlesData).slice(0, 5).map((columnName) => (
                             <th key={columnName} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                               {getColumnDisplayName(columnName)}
                             </th>
                           ))}
                           <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                         </tr>
                       </thead>
                       <tbody className="bg-white divide-y divide-gray-200">
                         {selectedArticlesData.map((article, index) => {
                           const articleId = String(article.id || article.ref);
                           return (
                             <tr key={articleId || index}>
                               {getDynamicColumns(selectedArticlesData).slice(0, 5).map((columnName) => (
                                 <td key={columnName} className="px-3 py-2 text-sm text-gray-900">
                                   {formatCellValue(article[columnName], columnName)}
                                 </td>
                               ))}
                               <td className="px-3 py-2 text-right text-sm">
                                 <button
                                   type="button"
                                   onClick={() => handleRemoveArticle(articleId)}
                                   className="text-red-600 hover:text-red-800 underline"
                                 >
                                   Retirer
                                 </button>
                               </td>
                             </tr>
                           );
                         })}
                       </tbody>
                     </table>
                   </div>
                 ) : (
                   <div className="border border-gray-200 rounded-lg p-4 text-center text-gray-500 text-sm">
                     Aucun article sélectionné. Utilisez les filtres ci-dessus pour ajouter des articles.
                   </div>
                 )}
               </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Mise à jour...' : 'Mettre à jour'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Affected Articles Modal */}
      {showAffectedArticlesModal && viewingPromotion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Articles affectés par "{viewingPromotion.name}" ({affectedArticles.length})
              </h3>
              <button
                onClick={() => setShowAffectedArticlesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {affectedArticles.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {getDynamicColumns(affectedArticles).map((columnName) => (
                        <th key={columnName} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {getColumnDisplayName(columnName)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {affectedArticles.map((article: any, index) => (
                      <tr key={article.ref || index}>
                        {getDynamicColumns(affectedArticles).map((columnName) => (
                          <td key={columnName} className="px-4 py-2 text-sm text-gray-900">
                            {formatCellValue(article[columnName], columnName)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Aucun article à afficher
                </div>
              )}
            </div>
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowAffectedArticlesModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default Promotions;
